import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { createCard, getUserCards } from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { CreditType, UserCard } from "../api/types";
import { generateCardOperationSign } from "../api/auth";

interface createCardActionParams {
  chainID: number;
  creditType: CreditType;
  signTypedData: (typedData: any) => Promise<string>;
}

/**
 * Creates a new user card if one does not already exist, signs the operation,
 * and updates the local card store with the created card.
 *
 * This function performs the following steps:
 * 1. Fetches existing user cards and updates the card store.
 * 2. If no card exists, signs a typed message and creates a new card.
 * 3. Verifies the card creation by fetching updated cards.
 * 4. Ensures that the newly created card belongs to the user (EOA).
 * 5. Updates the store with the new card if creation was successful.
 *
 * @param {createCardActionParams} params - Parameters for creating the card.
 * @param {number} params.chainID - The blockchain network ID.
 * @param {CreditType} params.creditType - Type of credit to be associated with the card.
 * @param {(typedData: any) => Promise<string>} params.signTypedData - Function to sign the typed data.
 *
 * @returns {Promise<{ success: boolean; message: string }>} - Result indicating success or failure and a message.
 */
export async function createCardAction({
  chainID,
  creditType,
  signTypedData,
}: createCardActionParams): Promise<{ success: boolean; message: string }> {
  const { eoaAddress } = useSDKConfigStore.getState();
  const cardStatusStore = useCardStatusStore.getState();

  if (!eoaAddress) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  try {
    // Step 0: Fetch user cards and set the initial one in store
    const userCardsResponse = await getUserCards(eoaAddress);
    const initialUserCard = userCardsResponse.data?.[0] || null;
    cardStatusStore._setUserCard(initialUserCard);
    let card: UserCard | null = initialUserCard;

    const eoa = eoaAddress as `0x${string}`;
    const cardID = "00000000-0000-0000-0000-000000000000"; // Used for signature as this is a new card
    const timestamp = Math.floor(Date.now() / 1000);
    const action = "createCard";

    console.log("[createCardAction] Step 2: Signing authorization...");
    const { signature } = await generateCardOperationSign(
      eoa,
      chainID,
      cardID,
      action,
      timestamp,
      signTypedData
    );

    const authJson = {
      cardID,
      action,
      version: "1",
      timestamp,
      chainID,
      sig: signature,
      userAddr: eoa,
    };

    const encodedAuth = btoa(JSON.stringify(authJson));

    // Step 1: If no card exists, create one
    if (!card) {
      console.log("[createCardAction] Creating card...");
      const createCardResponse = await createCard(
        eoaAddress,
        chainID,
        creditType,
        encodedAuth
      );
      card = { ...createCardResponse.data, cardID: createCardResponse.data.id };

      if (!card?.cardID) {
        return {
          success: false,
          message: "Failed to create card - invalid card ID received.",
        };
      }
    }

    // Step 4: Verify that the card was actually created
    console.log("[createCardAction] Step 4: Verifying card creation result...");
    const userCardsResponse2 = await getUserCards(eoaAddress);
    const updatedCard = userCardsResponse2.data?.find(
      (c) => c.cardID === card.cardID
    );

    // Update the store with the latest card
    cardStatusStore._setUserCard(updatedCard || null);

    if (!updatedCard) {
      return {
        success: false,
        message:
          "Card not found after creation - card creation may have failed.",
      };
    }

    if (
      updatedCard.creditProviderAddress.toLowerCase() !==
      eoaAddress.toLowerCase()
    ) {
      return {
        success: false,
        message:
          "Card creation failed - credit provider address does not match EOA address.",
      };
    }

    console.log("[createCardAction] Card creation completed successfully.");
    return {
      success: true,
      message: "Card created and linked successfully.",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[createCardAction] Error during card creation:", err);
    return {
      success: false,
      message,
    };
  }
}
